import React from 'react';

// Perfiles de color disponibles en variantes de azul
export type BlueVariant = 'sky' | 'blue' | 'indigo' | 'cyan' | 'darkBlue';

interface ServiceCardProps {
  imageSrc: string;
  imageAlt?: string;
  title: string;
  description: string;
  buttonText: string;
  onButtonClick?: () => void;
  variant?: BlueVariant;
}

// Mapa de estilos según la variante de azul elegida
const variantStyles: Record<BlueVariant, { button: string; border: string }> = {
  sky: {
    button: 'border-sky-400 text-sky-400 hover:bg-sky-400 hover:text-white',
    border: 'hover:border-sky-200',
  },
  blue: {
    button: 'border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white',
    border: 'hover:border-blue-200',
  },
  indigo: {
    button: 'border-indigo-500 text-indigo-500 hover:bg-indigo-500 hover:text-white',
    border: 'hover:border-indigo-200',
  },
  cyan: {
    button: 'border-cyan-500 text-cyan-500 hover:bg-cyan-500 hover:text-white',
    border: 'hover:border-cyan-200',
  },
  darkBlue: {
    button: 'border-slate-700 text-slate-700 hover:bg-slate-800 hover:text-white',
    border: 'hover:border-slate-300',
  },
};

export const ServiceCard: React.FC<ServiceCardProps> = ({
  imageSrc,
  imageAlt = 'Servicio',
  title,
  description,
  buttonText,
  onButtonClick,
  variant = 'sky', // Color por defecto (idéntico a la imagen)
}) => {
  const currentVariant = variantStyles[variant];

  return (
    <div
      className={`group w-full max-w-s bg-white rounded-3xl p-6 border border-slate-100 shadow-md 
        transition-all duration-300 ease-out 
        hover:scale-105 hover:-translate-y-1.5 hover:shadow-2xl ${currentVariant.border}
        flex flex-col items-center text-center cursor-pointer`}
    >
      {/* 1. Imagen del equipo / servicio */}
      <div className="w-48 h-40 flex items-center justify-center mb-4 overflow-hidden">
        <img
          src={imageSrc}
          alt={imageAlt}
          className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      {/* 2. Título principal en negrita */}
      <h3 className="text-slate-900 font-extrabold text-lg uppercase tracking-wide mb-3">
        {title}
      </h3>

      {/* 3. Descripción secundaria */}
      <p className="text-slate-500 text-xs leading-relaxed mb-6 grow">
        {description}
      </p>

      {/* 4. Botón redondeado en píldora con borde */}
      <button
        onClick={onButtonClick}
        className={`w-full py-2.5 px-4 rounded-full border-2 font-bold text-xs tracking-wide
          transition-colors duration-200 ${currentVariant.button}`}
      >
        {buttonText}
      </button>
    </div>
  );
};